/* eslint-disable max-len */
import { Accordion, AccordionDetails, Box, Button, Chip, CircularProgress, Grid, GridProps, Paper, Typography } from '@material-ui/core';
import Divider, { DividerProps } from '@material-ui/core/Divider';
import { Check } from '@material-ui/icons';
import {
	AddressSearchRequest,
	PropertyAddressSearchResultDto,
	useCreateInvoice,
	useCreateWorkflow,
	useGetDocumentById,
	useGetMailById,
	useGetPropertyId
} from 'api';
import { SnackbarHandlerContext } from 'context/SnackbarContext';
import { UserProfileContext } from 'context/UserProfileContext';
import { exists } from 'lib/utils/exists';
import _ from 'lodash';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'themed-components';
import { v4 as uuid } from 'uuid';
import { useDebounceRefetch } from '../../hooks/useDebounceRefetch';
import { AdvancedAddressSearch } from './AdvancedSearch';
import { InvoiceFields, InvoiceInputFields } from './InvoiceFields';
import { Toggle, ToggleOptions } from './InvoiceToggle';
import { PdfDisplay } from './PdfDisplay';
import { PropertySearchResults } from './PropertySearchResult';

// import createHistory from 'history/createBrowserHistory';
// import { Modal } from '@material-ui/core';

export interface MailReviewViewRouteProps {
	id?: string;
}

const StyledPaper = styled(Paper)`
	padding: 1rem;
`;

const StyledGrid = styled(Grid)`
	padding: 1rem;
`;

const StyledGridContainer = ({ children, ...props }: GridProps) => (
	<StyledGrid {...props} container>
		{children}
	</StyledGrid>
);

const StyledGridDivider = (props: DividerProps) => (
	<StyledGrid>
		<Grid item>
			<Divider variant="middle" {...props} />
		</Grid>
	</StyledGrid>
);
const ReturnHome = ({ onClick }: { onClick: () => void }) => {
	return (
		<StyledPaper>
			<Grid container direction="column" spacing={2}>
				<Grid item>No records found.</Grid>
				<Grid item>
					<Button variant="contained" color="primary" onClick={onClick}>
						Return Home
					</Button>
				</Grid>
			</Grid>
		</StyledPaper>
	);
};
export const MailReviewView = () => {
	// Route props
	const { id } = useParams<MailReviewViewRouteProps>();

	// Local states
	const [numPages, setNumPages] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [property, selectProperty] = useState<PropertyAddressSearchResultDto | undefined>(undefined);
	const [invoiceData, setInvoiceData] = useState<InvoiceInputFields | undefined>();
	const [propertySearch, setSearchTerms] = useState<AddressSearchRequest>({});
	const [mailType, setMailType] = useState<ToggleOptions | undefined>();

	const { messages: history, push } = useContext(SnackbarHandlerContext);
	const routeHisory = useHistory<string>();

	const {
		data: mailData,
		isFetched: isMailDataFetched,
		isLoading: mailDataIsLoading,
		isError,
		status,
		error
	} = useGetMailById({
		entityId: id
	});

	const {
		data: objectUrl,
		isLoading: documentLoading,
		refetch: refetchPdf
	} = useGetDocumentById(
		{ id: mailData?.documentId! },
		{
			enabled: exists(mailData) && exists(mailData?.documentId),
			retryOnMount: true
		}
	);

	const noMailData = !exists(mailData) && !mailDataIsLoading;

	const clearState = () => {
		selectProperty(undefined);
		setInvoiceData(undefined);
		setPageNumber(1);
		setNumPages(0);
		setSearchTerms({});
		window.scrollTo(0, 0);
	};
	useEffect(() => {
		const hasRouteId = !!id;
		const hasMailEntityId = !!mailData?.entityId;

		// If routeId exists, and mailEntityId doesnt exist, and we have fetched our data, get new.
		// This assumes we have received a no-content and nothing remains
		if (hasRouteId && !hasMailEntityId && isMailDataFetched) {
			routeHisory.push(`/mail`);
		}
		// If routeId and mailEntityId exist, but do not match, get new.
		if (hasRouteId && hasMailEntityId && mailData?.entityId !== id) {
			routeHisory.push(`/mail`);
		}
		// if no route id
		if (!hasRouteId) {
			// if data id
			clearState();
			if (mailData?.entityId) routeHisory.push(`/mail/${mailData.entityId}`);
		}
	}, [mailData, id, isMailDataFetched]);

	const { mutate: createWorkflowMutator, isLoading: isCreateWorkflowLoading } = useCreateWorkflow({
		onSuccess: () => {
			push('Successfully created workflow!', 'success');
			setTimeout(() => routeHisory.push(`/mail`), 500);
		},
		onError: (error) => {
			if (error && error.message) return push(error.message, 'error');
			push(`There was a problem creating the workflow.`, 'error');
		}
	});

	const { mutate: createInvoiceMutator, isLoading: isCreateInvoiceLoading } = useCreateInvoice({
		onSuccess: () => {
			push('Successfully created invoice!', 'success');
			setTimeout(() => routeHisory.push(`/mail`), 500);
		},
		onError: (error) => {
			if (error && error.message) return push(error.message, 'error');
			push(`There was a problem creating the invoice.`, 'error');
		}
	});

	const createInvoice = () => {
		if (!property?.propertyId) return;
		createInvoiceMutator({
			metadataId: mailData?.metadataId,
			documentId: mailData?.documentId,
			propertyEntityId: property?.entityId,
			invoice: {
				gst: invoiceData?.GST || 0.0,
				totalInvoiceAmount: invoiceData?.totalAmount || 0,
				amount: invoiceData?.amount,
				description: 'Mailroom Invoice',
				propertyId: property.propertyId,
				invoiceCategoryCode: 4002077,
				isPayableByRent: false,
				localDueDate: moment(invoiceData?.dueDate).format() || moment().format(),
				payableBy: 4003005,
				supplierUserProfileId: invoiceData?.supplier?.userProfileId,
				payableTo: 4004007,
				uploadedInvoiceDocumentId: mailData?.documentId,
				dueDate: moment(invoiceData?.dueDate).format() || moment().format(),
				lineItems: [
					{
						description: 'Invoice from mail',
						amount: invoiceData?.amount || 0,
						gst: invoiceData?.GST || 0
					}
				]
			}
		});
	};

	const createWorkflow = () => {
		if (!property?.propertyId) return;
		createWorkflowMutator({
			documentId: mailData?.documentId,
			metadataId: mailData?.metadataId,
			propertyEntityId: property?.entityId,
			workflow: {
				workflowGuid: uuid(),
				workflowTypeId: 33,
				subject: 'Mail',
				description: 'Incoming mail',
				propertyId: property?.propertyId,
				assignedToProfileId: property?.propertyManagerUserProfileEntityId
			}
		});
	};

	const send = () => {
		if (!mailType) return;

		switch (mailType) {
			case ToggleOptions.Invoice: {
				createInvoice();
				break;
			}
			case ToggleOptions.NonInvoice: {
				createWorkflow();
				break;
			}
		}
	};
	const initialLoading = mailDataIsLoading || documentLoading;

	function onDocumentLoadSuccess({ numPages }: any) {
		setNumPages(numPages);
	}

	const onSaveNextClick = (entityId?: string) => {
		send();
	};

	const onToggleChange = (t: ToggleOptions) => {
		setMailType(t);
	};
	const { userId } = useContext(UserProfileContext);
	const {
		data: pagedPropertyData,
		refetch,
		isLoading: isPropertySearchLoading,
		abort = _.noop,
		isFetching: isPropertySearchFetching
	} = useGetPropertyId(
		{
			propertySearch: propertySearch
		},
		{
			refetchOnMount: false,
			enabled: false,
			onError: (_) => {
				push('failed to search properties', 'error');
			}
		}
	);
	const data = pagedPropertyData?.results;

	const { exec } = useDebounceRefetch(refetch, propertySearch, abort);

	useEffect(() => {
		if (isPropertySearchLoading || isPropertySearchFetching) return;
		if (!propertySearch) return;

		if (!_.every(propertySearch, (x) => _.isEmpty(x))) exec();
		return () => abort();
	}, [propertySearch]);

	const onAddressChange = (a: AddressSearchRequest) => {
		setSearchTerms(a);
	};

	const onAddressSelect = (entity?: PropertyAddressSearchResultDto) => {
		selectProperty(entity);
	};

	if (!mailData && isPropertySearchLoading)
		return (
			<StyledPaper>
				<CircularProgress />
			</StyledPaper>
		);
	if (noMailData) return <ReturnHome onClick={() => routeHisory.push('/')} />;
	if (!!userId && mailData?.lockedByUserId && userId !== mailData?.lockedByUserId) {
		return (
			<StyledPaper>
				<Typography variant="h3">This item is locked.</Typography>
			</StyledPaper>
		);
	}
	const officeEntityId = _.get(property, 'associatedOfficeEntityId');

	return (
		<StyledPaper>
			<Grid direction="column" container spacing={2}>
				<StyledGridContainer justify="center" direction="row">
					<Grid xs={6} item>
						<PdfDisplay
							refetch={async () => {
								await refetchPdf();
							}}
							documentLoading={initialLoading}
							url={objectUrl?.documentUri}
							onLoaded={onDocumentLoadSuccess}
							pageNumber={pageNumber}
							setPageNumber={setPageNumber}
							numPages={numPages}
						/>
					</Grid>

					{(isCreateInvoiceLoading || isCreateWorkflowLoading) && (
						<Grid xs={6} container alignItems="center" justify="center" item direction="column">
							<Grid xs={12} item component={Box}>
								<CircularProgress></CircularProgress>
							</Grid>
						</Grid>
					)}
					{!isCreateInvoiceLoading && !isCreateWorkflowLoading && (
						<Grid xs={6} container item direction="column">
							<Accordion expanded={!property}>
								<AccordionDetails>
									<Grid xs={12} item component={Box}>
										{!property && (
											<>
												<AdvancedAddressSearch onChange={onAddressChange} />
												<PropertySearchResults
													selectedValue={property}
													onChange={onAddressSelect}
													loading={isPropertySearchLoading || isPropertySearchFetching}
													data={data}
												/>
											</>
										)}
										{!!property && (
											<Chip
												color="primary"
												size="medium"
												avatar={<Check style={{ backgroundColor: 'inherit' }} />}
												label={`${property.propertyName}`}
												onDelete={() => selectProperty(undefined)}
											/>
										)}
									</Grid>
								</AccordionDetails>
							</Accordion>
							<Accordion expanded={!mailType}>
								<AccordionDetails>
									{!!mailType && (
										<Chip
											color="primary"
											size="medium"
											avatar={<Check style={{ backgroundColor: 'inherit' }} />}
											label={`${mailType == ToggleOptions.Invoice ? 'Invoice' : 'Non Invoice'}`}
											onDelete={() => setMailType(undefined)}
										/>
									)}
									{!mailType && <Toggle onChange={onToggleChange} />}
								</AccordionDetails>
							</Accordion>
							{mailType == ToggleOptions.Invoice && (
								<Accordion expanded={!!property}>
									<AccordionDetails>
										<InvoiceFields officeEntityId={officeEntityId} onChange={setInvoiceData} />
									</AccordionDetails>
								</Accordion>
							)}
						</Grid>
					)}
				</StyledGridContainer>
				<StyledGridDivider variant="middle" />
				<StyledGridContainer alignItems="flex-end" spacing={2}>
					<Grid xs={6} justify="space-between" container item>
						<Grid item />
						<Grid item>
							{/* <Button disabled variant="contained" color="secondary">
								escalate
							</Button> */}
						</Grid>
					</Grid>
					<Grid xs={6} justify="flex-end" container item>
						<Button
							disabled={isCreateInvoiceLoading || isCreateWorkflowLoading}
							onClick={() => mailData && onSaveNextClick(mailData?.entityId)}
							variant="contained"
							color="primary"
						>
							{'Save & Next'}
						</Button>
					</Grid>
				</StyledGridContainer>
			</Grid>
		</StyledPaper>
	);
};
